import { useRef, useState, type FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Botao } from '../../componentes/base';
import { CampoControlado, prontoParaConsulta } from '../../componentes/formulario';
import { Modal } from '../../componentes/sobreposicao';
import { useToast } from '../../componentes/toast';
import { useValidacaoLocal } from '../../componentes/validacao-local';
import { api, ErroRequisicao, get, mensagemDeErro, post } from '../../nucleo/api';

/** Resposta de `GET /cadastros/consultas/cnpj/:cnpj` (o que o cadastro rápido usa). */
interface Consulta {
  habilitada: boolean;
  situacao: 'encontrado' | 'nao_encontrado' | 'falha' | 'limite' | null;
  dados: Record<string, unknown> | null;
}
type Estado = 'buscando' | 'preenchido' | 'naoEncontrado' | 'falha' | 'desligada';

const texto = (v: unknown) => (typeof v === 'string' && v.trim() ? v.trim() : null);
/** Os dados fiscais da Receita no formato do `PUT /cadastros/parceiros/:id/fiscal` (só os que vieram). */
const FISCAIS = ['razao_social', 'nome_fantasia', 'data_abertura', 'porte', 'natureza_juridica', 'situacao_cadastral', 'situacao_data', 'capital_social_centavos'] as const;
function fiscalDe(d: Record<string, unknown>) {
  const r: Record<string, unknown> = {};
  for (const k of FISCAIS) if (d[k] !== null && d[k] !== undefined && d[k] !== '') r[k] = d[k];
  return Object.keys(r).length ? r : null;
}
function enderecoDe(d: Record<string, unknown>) {
  const e = (d.endereco ?? null) as Record<string, unknown> | null;
  if (!e) return null;
  const r = { cep: texto(e.cep), logradouro: texto(e.logradouro), numero: texto(e.numero), complemento: texto(e.complemento), bairro: texto(e.bairro), cidade: texto(e.cidade), uf: texto(e.uf) };
  return Object.values(r).some(Boolean) ? r : null;
}

/**
 * Item 12 (ficha 2026-10-21 "Rotina comercial"): o lead criado na hora pelo CNPJ, sem sair do formulário da oportunidade. A
 * consulta do CNPJ é a do cadastro (`GET /cadastros/consultas/cnpj/:cnpj`, conector BrasilAPI): preenche nome, e-mail e
 * telefone vazios e guarda endereço e dados fiscais para gravar junto. Grava pela rota do Cadastros (`POST
 * /cadastros/parceiros`, `cadastros.criar`) como lead (`e_cliente` e situação `lead`; no ganho vira cliente sozinho); CNPJ
 * já cadastrado oferece usar o cadastro existente. Mesmo padrão do cadastro rápido de fornecedor (`novo-fornecedor.tsx`).
 */
export function NovoLead({ aberto, aoFechar, aoCriar }: { aberto: boolean; aoFechar: () => void; aoCriar: (p: { id: string; nome: string }) => void }) {
  if (!aberto) return null;
  return <Corpo aoFechar={aoFechar} aoCriar={aoCriar} />;
}

function Corpo({ aoFechar, aoCriar }: { aoFechar: () => void; aoCriar: (p: { id: string; nome: string }) => void }) {
  const { t } = useTranslation('pipeline');
  const { t: tn } = useTranslation();
  const toast = useToast();
  const [id] = useState(() => crypto.randomUUID());
  const [cnpj, setCnpj] = useState('');
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [telefone, setTelefone] = useState('');
  const [estado, setEstado] = useState<Estado | null>(null);
  const [erro, setErro] = useState<string | null>(null);
  const [erros, setErros] = useState<Record<string, string>>({});
  const [existente, setExistente] = useState<string | null>(null);
  const { ref } = useValidacaoLocal(erros, erro);
  const ultima = useRef('');
  const dados = useRef<Record<string, unknown> | null>(null);

  async function consultar(valor: string) {
    setEstado('buscando');
    dados.current = null;
    let r: Consulta;
    try {
      r = await get<Consulta>(`/cadastros/consultas/cnpj/${valor}`);
    } catch {
      r = { habilitada: true, situacao: 'falha', dados: null };
    }
    if (ultima.current !== valor) return;
    if (!r.habilitada) return setEstado('desligada');
    if (r.situacao !== 'encontrado' || !r.dados) return setEstado(r.situacao === 'nao_encontrado' ? 'naoEncontrado' : 'falha');
    dados.current = r.dados;
    // Só preenche o que está vazio: o que a pessoa digitou fica.
    setNome((x) => x || texto(r.dados!.razao_social) || texto(r.dados!.nome_fantasia) || '');
    setEmail((x) => x || texto(r.dados!.email) || '');
    setTelefone((x) => x || texto(r.dados!.telefone) || '');
    setEstado('preenchido');
  }

  function mudarCnpj(v: string) {
    setCnpj(v);
    setExistente(null);
    const pronto = prontoParaConsulta({ tipo: 'documento' }, v);
    if (!pronto || pronto === ultima.current) return;
    ultima.current = pronto;
    void consultar(pronto);
  }

  const criar = useMutation({
    mutationFn: async () => {
      const d = dados.current;
      try {
        await post('/cadastros/parceiros', {
          id,
          tipo: 'empresa',
          nome: nome.trim(),
          documento: cnpj.trim() || null,
          email: email.trim() || null,
          telefone: telefone.trim() || null,
          e_cliente: true,
          situacao_comercial: 'lead',
          origem: 'pipeline',
          endereco: d ? enderecoDe(d) : null,
        });
      } catch (e) {
        const meta = e instanceof ErroRequisicao && e.corpo.code === 'parceiro.documento_duplicado' ? e.metaDoErro() : undefined;
        if (meta && typeof meta.id === 'string') setExistente(meta.id);
        throw e;
      }
      // Dados fiscais depois de criar, sem travar o lead (a falha só avisa), como no cadastro completo.
      const fiscal = d ? fiscalDe(d) : null;
      if (fiscal) await api('PUT', `/cadastros/parceiros/${id}/fiscal`, fiscal).catch((e: unknown) => toast.erro(mensagemDeErro(e)));
      return { id, nome: nome.trim() };
    },
    onSuccess: (p) => {
      toast.sucesso(t('leadRapido.criado'));
      aoCriar(p);
      aoFechar();
    },
    onError: (e) => {
      setErros(e instanceof ErroRequisicao ? e.porCampo() : {});
      setErro(mensagemDeErro(e));
    },
  });

  const usar = useMutation({
    mutationFn: async (pid: string) => ({ id: pid, nome: (await get<{ nome: string }>(`/cadastros/parceiros/${pid}`)).nome }),
    onSuccess: (p) => {
      aoCriar(p);
      aoFechar();
    },
    onError: (e) => setErro(mensagemDeErro(e)),
  });

  function enviar(ev: FormEvent) {
    ev.preventDefault();
    // O modal fica num portal: sem isto, o submit sobe pela árvore do React até o formulário da oportunidade.
    ev.stopPropagation();
    setErro(null);
    setErros({});
    criar.mutate();
  }

  return (
    <Modal
      aberto
      aoFechar={aoFechar}
      titulo={t('leadRapido.titulo')}
      rodape={
        <>
          <Botao variante="fantasma" onClick={aoFechar}>
            {tn('comum.cancelar')}
          </Botao>
          <Botao variante="primario" type="submit" form="form-lead-rapido" carregando={criar.isPending}>
            {t('leadRapido.salvar')}
          </Botao>
        </>
      }
    >
      <form ref={ref} id="form-lead-rapido" onSubmit={enviar} noValidate className="flex flex-col gap-4">
        <p className="text-sm text-tinta-3">{t('leadRapido.explicacao')}</p>
        {erro && (
          <div role="alert" className="flex flex-col gap-2 rounded-lg border border-perigo px-3 py-2 text-sm text-perigo">
            <p>{erro}</p>
            {existente && (
              <Botao className="self-start" carregando={usar.isPending} onClick={() => usar.mutate(existente)}>
                {t('leadRapido.usarExistente')}
              </Botao>
            )}
          </div>
        )}
        <CampoControlado
          def={{ nome: 'documento', rotulo: t('leadRapido.cnpj'), tipo: 'documento', documento: 'cnpj', ajuda: estado ? t(`leadRapido.consulta.${estado}`) : t('leadRapido.cnpjAjuda') }}
          valor={cnpj}
          aoMudar={(v) => mudarCnpj(String(v ?? ''))}
          erro={erros.documento}
        />
        <CampoControlado def={{ nome: 'nome', rotulo: t('leadRapido.nome'), tipo: 'texto', obrigatorio: true, sensivel: 'nome' }} valor={nome} aoMudar={(v) => setNome(String(v ?? ''))} erro={erros.nome} />
        <CampoControlado def={{ nome: 'email', rotulo: t('leadRapido.email'), tipo: 'email' }} valor={email} aoMudar={(v) => setEmail(String(v ?? ''))} erro={erros.email} />
        <CampoControlado def={{ nome: 'telefone', rotulo: t('leadRapido.telefone'), tipo: 'telefone' }} valor={telefone} aoMudar={(v) => setTelefone(String(v ?? ''))} erro={erros.telefone} />
      </form>
    </Modal>
  );
}
